import { useCallback, useState } from "react";

// A choice about this window rather than about this page — whether the navbar is showing, whether the width cap is
// lifted — which now has to outlive a document rather than a route change, every move between utilities being a fresh
// load. Session storage is this tab's own and goes when it does, which is exactly as long as the choice is worth
// keeping; a browser that refuses it makes the choice again on the next page, and nothing else is lost.
export function useTabFlag(key: string, fallback: boolean): [boolean, () => void] {
  const [value, setValue] = useState(() => stored(key) ?? fallback);

  const toggle = useCallback(() => {
    setValue((current) => {
      keep(key, !current);
      return !current;
    });
  }, [key]);

  return [value, toggle];
}

function stored(key: string): boolean | undefined {
  try {
    const kept = sessionStorage.getItem(key);
    return kept === null ? undefined : kept === "true";
  } catch {
    return undefined;
  }
}

function keep(key: string, value: boolean): void {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // A tab that will not keep it is one where the choice is made again, which is no reason to fail a render.
  }
}
